'use client';

import React, { useState, useEffect, FormEvent, Suspense } from 'react';
import { useSearchParams, useRouter } from 'next/navigation';
import Link from 'next/link';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import type { StripeElementsOptions } from '@stripe/stripe-js';
import { getStripe } from '@/lib/stripe/client';
import { createClient } from '@/lib/supabase/client';
import AppLogo from '@/components/ui/AppLogo';

// ─── Tier Config ──────────────────────────────────────────────────────────────
interface TierConfig {
  name: string;
  icon: string;
  monthlyPrice: number;
  annualPrice: number;
  color: string;
  gradient: string;
  features: string[];
}

const TIER_CONFIG: Record<string, TierConfig> = {
  student: {
    name: 'Student',
    icon: '📖',
    monthlyPrice: 15,
    annualPrice: 12,
    color: 'text-blue-600',
    gradient: 'from-blue-500 to-indigo-600',
    features: ['All 5 prayers tracked', 'Unlimited habit tracker', 'AI coaching (text)', 'Weekly progress report'],
  },
  undergraduate: {
    name: 'Undergraduate',
    icon: '🎓',
    monthlyPrice: 20,
    annualPrice: 16,
    color: 'text-primary',
    gradient: 'from-teal-500 to-emerald-600',
    features: ['Everything in Student', 'Voice AI coaching', 'Academic Research Mode', 'Ambassador rewards'],
  },
  seniors: {
    name: 'Seniors',
    icon: '🕌',
    monthlyPrice: 25,
    annualPrice: 20,
    color: 'text-emerald-600',
    gradient: 'from-emerald-500 to-teal-600',
    features: ['Everything in Undergraduate', 'Scholar Directory access', 'Personal scholar Q&A', 'Priority support'],
  },
  premium: {
    name: 'Premium',
    icon: '🚀',
    monthlyPrice: 30,
    annualPrice: 24,
    color: 'text-amber-600',
    gradient: 'from-amber-500 to-orange-600',
    features: ['Everything in Undergraduate', 'Business strategy coaching', 'Rafeeq Academic Research Assistant', 'White-glove support'],
  },
};

// ─── Order Summary ────────────────────────────────────────────────────────────
function OrderSummary({ tier, annual }: { tier: string; annual: boolean }) {
  const config = TIER_CONFIG[tier] || TIER_CONFIG.student;
  const price = annual ? config.annualPrice : config.monthlyPrice;
  const annualTotal = price * 12;

  return (
    <div className="bg-card border border-border rounded-2xl p-6 sticky top-6">
      <h2 className="text-base font-700 text-foreground mb-5">Order Summary</h2>

      {/* Plan header */}
      <div className={`bg-gradient-to-r ${config.gradient} rounded-xl p-4 text-white mb-5`}>
        <div className="flex items-center gap-3">
          <span className="text-2xl">{config.icon}</span>
          <div>
            <p className="text-white/70 text-xs font-500 uppercase tracking-wide">Rafeeq</p>
            <p className="text-white font-700 text-lg">{config.name} Plan</p>
          </div>
        </div>
        <div className="mt-3 pt-3 border-t border-white/20">
          <div className="flex items-end gap-1">
            <span className="text-2xl font-800">${price}</span>
            <span className="text-white/70 text-sm mb-0.5">/mo</span>
          </div>
          {annual && (
            <p className="text-white/70 text-xs mt-0.5">Billed ${annualTotal}/year · Save 20%</p>
          )}
          {!annual && (
            <p className="text-white/70 text-xs mt-0.5">Billed monthly · cancel anytime</p>
          )}
        </div>
      </div>

      {/* Features */}
      <ul className="space-y-2.5 mb-5">
        {config.features.map((f) => (
          <li key={f} className="flex items-start gap-2.5 text-sm text-foreground">
            <svg className="w-4 h-4 mt-0.5 flex-shrink-0 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
            </svg>
            {f}
          </li>
        ))}
      </ul>

      {/* Price breakdown */}
      <div className="border-t border-border pt-4 space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">{config.name} Plan ({annual ? 'Annual' : 'Monthly'})</span>
          <span className="text-foreground font-500">${price}/mo</span>
        </div>
        {annual && (
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Annual billing</span>
            <span className="text-emerald-600 font-500">-20%</span>
          </div>
        )}
        <div className="flex justify-between text-base font-700 pt-2 border-t border-border">
          <span className="text-foreground">Total today</span>
          <span className="text-foreground">${annual ? annualTotal : price}</span>
        </div>
        <p className="text-xs text-muted-foreground">
          {annual ? `$${annualTotal} charged now, then annually` : `$${price} charged now, then monthly`}
        </p>
      </div>

      {/* Security badges */}
      <div className="mt-5 flex items-center gap-2 text-xs text-muted-foreground">
        <svg className="w-4 h-4 text-emerald-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
        </svg>
        Secured by Stripe · 256-bit SSL encryption
      </div>
    </div>
  );
}

// ─── Payment Form ─────────────────────────────────────────────────────────────
interface PaymentFormProps {
  clientSecret: string;
  tier: string;
  annual: boolean;
  userId: string;
  onSuccess: (paymentIntentId: string) => void;
  onError: (msg: string) => void;
}

function PaymentForm({ clientSecret, tier, annual, userId, onSuccess, onError }: PaymentFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const supabase = createClient();

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setIsProcessing(true);

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/payment-confirmation?tier=${tier}&annual=${annual}`,
      },
      redirect: 'if_required',
    });

    if (error) {
      console.error('Payment confirmation error:', error);
      onError(error.message || 'Payment failed. Please try again.');
      setIsProcessing(false);
      return;
    }

    if (paymentIntent?.status === 'succeeded') {
      // Confirm on backend
      const { error: confirmError } = await supabase.functions.invoke('confirm-stripe-payment', {
        body: { paymentIntentId: paymentIntent.id, userId, tier, annual },
      });

      if (confirmError) {
        console.error('Backend confirmation error:', confirmError);
        // Payment succeeded on Stripe, webhook will handle it — still navigate to success
      }

      onSuccess(paymentIntent.id);
    } else if (paymentIntent?.status === 'requires_action') {
      onError('Additional authentication required. Please complete the 3D Secure step.');
      setIsProcessing(false);
    } else {
      onError('Payment could not be completed. Please try again.');
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div>
        <label className="block text-sm font-600 text-foreground mb-3">Card Details</label>
        <div className="border border-border rounded-xl p-4 bg-background focus-within:border-primary transition-colors">
          <PaymentElement
            options={{
              layout: 'tabs',
              fields: {
                billingDetails: {
                  address: {
                    country: 'auto',
                  },
                },
              },
            }}
          />
        </div>
      </div>

      <button
        type="submit"
        disabled={!stripe || !elements || isProcessing}
        className="w-full py-4 px-6 gradient-teal text-white font-700 text-base rounded-xl hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2.5 shadow-prayer"
      >
        {isProcessing ? (
          <>
            <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            Processing payment…
          </>
        ) : (
          <>
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
            </svg>
            Confirm & Pay
          </>
        )}
      </button>

      <p className="text-center text-xs text-muted-foreground">
        By confirming, you agree to our{' '}
        <Link href="/home" className="text-primary hover:underline">Terms of Service</Link>
        {' '}and authorize this recurring charge. Cancel anytime from Settings.
      </p>
    </form>
  );
}

// ─── Checkout Inner ───────────────────────────────────────────────────────────
function CheckoutInner() {
  const searchParams = useSearchParams();
  const router = useRouter();
  const supabase = createClient();

  const tier = searchParams.get('tier') || 'student';
  const annual = searchParams.get('annual') === 'true';

  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [userId, setUserId] = useState<string>('');
  const [userEmail, setUserEmail] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [setupError, setSetupError] = useState<string | null>(null);
  const [paymentError, setPaymentError] = useState<string | null>(null);

  const config = TIER_CONFIG[tier] || TIER_CONFIG.student;

  useEffect(() => {
    async function initCheckout() {
      setIsLoading(true);
      setSetupError(null);

      // Get authenticated user
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        router.push('/sign-up-login-screen?redirect=/checkout?tier=' + tier + '&annual=' + annual);
        return;
      }

      setUserId(user.id);
      setUserEmail(user.email || '');

      // Get user profile for name
      const { data: profile } = await supabase
        .from('profiles')
        .select('full_name')
        .eq('id', user.id)
        .maybeSingle();

      const nameParts = (profile?.full_name || '').split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      // Create payment intent via edge function
      const { data, error } = await supabase.functions.invoke<{ clientSecret: string; stripeCustomerId: string }>('create-stripe-payment-intent', {
        body: {
          userId: user.id,
          email: user.email,
          firstName,
          lastName,
          tier,
          annual,
        },
      });

      if (error || !data?.clientSecret) {
        console.error('Payment intent creation failed:', error);
        setSetupError((data as { error?: string })?.error ?? error?.message ?? 'Failed to initialize payment. Please try again.');
        setIsLoading(false);
        return;
      }

      setClientSecret(data.clientSecret);
      setIsLoading(false);
    }

    initCheckout();
  }, [tier, annual]);

  const handleSuccess = (paymentIntentId: string) => {
    router.push(
      `/payment-confirmation?tier=${tier}&annual=${annual}&status=success&order_id=${paymentIntentId}`
    );
  };

  const handleError = (msg: string) => {
    setPaymentError(msg);
  };

  const stripePromise = getStripe();

  const elementsOptions: StripeElementsOptions = clientSecret
    ? {
        clientSecret,
        appearance: {
          theme: 'stripe',
          variables: {
            colorPrimary: '#0d9488',
            colorBackground: '#ffffff',
            colorText: '#1a1a2e',
            colorDanger: '#ef4444',
            fontFamily: 'Inter, system-ui, sans-serif',
            spacingUnit: '4px',
            borderRadius: '10px',
          },
        },
      }
    : {};

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-card/90 backdrop-blur-md border-b border-border">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link href="/pricing" className="flex items-center gap-2.5">
            <AppLogo size={28} />
            <span className="font-700 text-base text-foreground">Rafeeq</span>
          </Link>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <svg className="w-4 h-4 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
            </svg>
            Secure checkout
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-10">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-8">
          <Link href="/pricing" className="hover:text-foreground transition-colors">Plans</Link>
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
          </svg>
          <span className="text-foreground font-500">Checkout</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Left: Payment form */}
          <div className="lg:col-span-3">
            <div className="bg-card border border-border rounded-2xl p-6 md:p-8">
              {/* Plan badge */}
              <div className="flex items-center gap-3 mb-6 pb-6 border-b border-border">
                <span className="text-2xl">{config.icon}</span>
                <div>
                  <p className="text-xs text-muted-foreground font-500 uppercase tracking-wide">Upgrading to</p>
                  <p className="text-lg font-700 text-foreground">{config.name} Plan</p>
                </div>
                <div className="ml-auto text-right">
                  <p className="text-2xl font-800 text-foreground">
                    ${annual ? config.annualPrice : config.monthlyPrice}
                    <span className="text-sm font-500 text-muted-foreground">/mo</span>
                  </p>
                  {annual && (
                    <p className="text-xs text-emerald-600 font-500">Annual · Save 20%</p>
                  )}
                </div>
              </div>

              {/* User info */}
              {userEmail && (
                <div className="mb-5 p-3.5 bg-muted/50 rounded-xl flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full gradient-teal flex items-center justify-center text-white text-sm font-700">
                    {userEmail[0].toUpperCase()}
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Paying as</p>
                    <p className="text-sm font-500 text-foreground">{userEmail}</p>
                  </div>
                  <Link href="/sign-up-login-screen" className="ml-auto text-xs text-primary hover:underline">
                    Switch
                  </Link>
                </div>
              )}

              {/* Loading state */}
              {isLoading && (
                <div className="space-y-4">
                  <div className="h-4 bg-muted rounded animate-pulse w-1/3" />
                  <div className="h-12 bg-muted rounded-xl animate-pulse" />
                  <div className="h-12 bg-muted rounded-xl animate-pulse" />
                  <div className="h-12 bg-muted rounded-xl animate-pulse" />
                  <div className="h-14 bg-muted rounded-xl animate-pulse mt-6" />
                </div>
              )}

              {/* Setup error */}
              {setupError && !isLoading && (
                <div className="rounded-xl bg-red-50 border border-red-200 p-4 mb-4">
                  <div className="flex items-start gap-3">
                    <svg className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                    </svg>
                    <div>
                      <p className="text-sm font-600 text-red-700">Setup failed</p>
                      <p className="text-sm text-red-600 mt-0.5">{setupError}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => window.location.reload()}
                    className="mt-3 text-sm font-600 text-red-700 hover:text-red-800 underline"
                  >
                    Try again
                  </button>
                </div>
              )}

              {/* Payment error */}
              {paymentError && (
                <div className="rounded-xl bg-red-50 border border-red-200 p-4 mb-4">
                  <div className="flex items-start gap-3">
                    <svg className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                    </svg>
                    <div>
                      <p className="text-sm font-600 text-red-700">Payment failed</p>
                      <p className="text-sm text-red-600 mt-0.5">{paymentError}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setPaymentError(null)}
                    className="mt-2 text-xs text-red-600 hover:text-red-700 underline"
                  >
                    Dismiss
                  </button>
                </div>
              )}

              {/* Stripe Elements */}
              {!isLoading && !setupError && clientSecret && (
                <Elements stripe={stripePromise} options={elementsOptions}>
                  <PaymentForm
                    clientSecret={clientSecret}
                    tier={tier}
                    annual={annual}
                    userId={userId}
                    onSuccess={handleSuccess}
                    onError={handleError}
                  />
                </Elements>
              )}
            </div>

            {/* Back link */}
            <div className="mt-4 text-center">
              <Link href="/pricing" className="text-sm text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1.5">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
                </svg>
                Back to pricing
              </Link>
            </div>
          </div>

          {/* Right: Order summary */}
          <div className="lg:col-span-2">
            <OrderSummary tier={tier} annual={annual} />
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function CheckoutPage() {
  return (
    <Suspense fallback={
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-3">
          <div className="w-10 h-10 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-sm text-muted-foreground">Loading checkout…</p>
        </div>
      </div>
    }>
      <CheckoutInner />
    </Suspense>
  );
}
